import { useState, type SyntheticEvent } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

function initialCrop(aspect: number | undefined, width: number, height: number): Crop {
  if (aspect) return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height), width, height);
  return { unit: '%', x: 5, y: 5, width: 90, height: 90 };
}

export interface ImageCropModalProps {
  src: string;
  /** Locks the crop box to a fixed ratio (e.g. 21/9). Omit for a freely resizable box. */
  aspect?: number;
  /** Neither exported dimension exceeds this — downscale only, never upscale. */
  maxDimension: number;
  title: string;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

/** Crop-then-convert-to-WebP dialog, reused for the Hero and QR content images. */
export default function ImageCropModal({ src, aspect, maxDimension, title, onCancel, onConfirm }: ImageCropModalProps) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImage(img);
    setCrop(initialCrop(aspect, img.width, img.height));
  };

  const confirm = async () => {
    if (!image || !completedCrop || !completedCrop.width || !completedCrop.height) return;
    setBusy(true);
    setErr('');
    try {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const sx = completedCrop.x * scaleX;
      const sy = completedCrop.y * scaleY;
      const sw = completedCrop.width * scaleX;
      const sh = completedCrop.height * scaleY;
      const scale = Math.min(1, maxDimension / Math.max(sw, sh));

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sw * scale));
      canvas.height = Math.max(1, Math.round(sh * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.85));
      if (!blob) throw new Error('toBlob failed');
      onConfirm(blob);
    } catch {
      setErr('ครอบตัดรูปไม่สำเร็จ กรุณาลองใหม่');
      setBusy(false);
    }
  };

  return (
    <div className="sa-modal-overlay" onClick={onCancel}>
      <div className="sa-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="sa-modal-head">
          <div>
            <div className="title">{title}</div>
            <div className="sub">ลากขอบเพื่อปรับพื้นที่ครอบตัด — บันทึกเป็น WebP อัตโนมัติ</div>
          </div>
        </div>
        <div className="sa-modal-body">
          {err && <div className="form-error" style={{ marginBottom: 12 }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'center', background: 'var(--bg)', borderRadius: 10, padding: 12 }}>
            <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} onComplete={setCompletedCrop} aspect={aspect} keepSelection>
              {/* eslint-disable-next-line jsx-a11y/alt-text */}
              <img src={src} onLoad={onImageLoad} style={{ maxHeight: '60vh', display: 'block' }} />
            </ReactCrop>
          </div>
        </div>
        <div className="sa-modal-foot">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>ยกเลิก</button>
          <button type="button" className="btn-primary" onClick={confirm} disabled={busy || !completedCrop}>
            {busy ? 'กำลังประมวลผล…' : 'ครอบตัดและอัปโหลด'}
          </button>
        </div>
      </div>
    </div>
  );
}
